import type { MoneyRecord } from '@navio/nsdk';
import {
  CarTireRecord,
  CarTireRecordList,
  LoadIndexRecord,
  SpeedIndexRecord,
  TireQuoteItemTireRecord,
  TireQuoteItemTireRecordCustomer
} from '@navio/nsdk';
import type { TireInQuoteItem } from 'noddi-types';

/** Tire specification format aligned with backend: (\\d{3})/(\\d{2})(\\D)(\\d{2}(?:\\.\\d{2})?) */
export const tireDimensionRegex = /^(\d{3})\/(\d{2})(\D)(\d{2}(?:\.\d{2})?)$/;

const tireDimensionInTextRegex = /(\d{3}\/\d{2}\D\d{2}(?:\.\d{2})?)/g;

// Mirrors backend TireSpecification bounds (ncom/vehicles/tire/validators.py). The regex alone lets
// placeholder values like 000/00R00 through, which the backend rejects with tire_parse_error.
const TIRE_DIMENSION_BOUNDS = {
  width: { min: 25, max: 999 },
  aspectRatio: { min: 10, max: 100 },
  rimInch: { min: 8, max: 87 }
} as const;

export type TireDimensionParts = {
  widthMm: string;
  aspectRatio: string;
  construction: string;
  rimInch: string;
};

type TireDimensionBreakdownItem = {
  meaning: string;
  value: string;
  extra?: string;
};

type TireDimensionTextPart = {
  type: 'text' | 'dimension';
  value: string;
};

const TIRE_CONSTRUCTIONS = ['R', 'D', 'B'] as const;
export type TireConstruction = (typeof TIRE_CONSTRUCTIONS)[number];
const DEFAULT_TIRE_CONSTRUCTION: TireConstruction = 'R';

const TIRE_CONSTRUCTION_LABELS: Record<TireConstruction, string> = {
  R: 'radial',
  D: 'diagonal',
  B: 'bias-belted'
};

export const TIRE_CONSTRUCTION_OPTIONS = TIRE_CONSTRUCTIONS.map((value) => ({
  value,
  label: `${value} (${TIRE_CONSTRUCTION_LABELS[value]})`
}));

const isTireConstruction = (value: string): value is TireConstruction => {
  return TIRE_CONSTRUCTIONS.some((item) => item === value);
};

export const detectTireConstruction = (value: string): TireConstruction => {
  const match = value.toUpperCase().match(/[RDB]/);
  if (match && isTireConstruction(match[0])) {
    return match[0];
  }
  return DEFAULT_TIRE_CONSTRUCTION;
};

export const parseTireDimension = (label: string): TireDimensionParts | null => {
  const match = tireDimensionRegex.exec(label.trim());
  const widthMm = match?.[1];
  const aspectRatio = match?.[2];
  const construction = match?.[3];
  const rimInch = match?.[4];
  if (!widthMm || !aspectRatio || !construction || !rimInch) {
    return null;
  }
  return { widthMm, aspectRatio, construction, rimInch };
};

/** Returns why a tire specification would be rejected by the backend, or null when it is valid. */
export const getTireDimensionError = (value: string): string | null => {
  const parts = parseTireDimension(value);
  if (!parts) {
    return 'Enter a full size, e.g. 205/55R16';
  }
  const width = Number(parts.widthMm);
  const aspectRatio = Number(parts.aspectRatio);
  const rimInch = Math.trunc(Number(parts.rimInch));
  if (width < TIRE_DIMENSION_BOUNDS.width.min || width > TIRE_DIMENSION_BOUNDS.width.max) {
    return `Width must be ${TIRE_DIMENSION_BOUNDS.width.min}-${TIRE_DIMENSION_BOUNDS.width.max} mm`;
  }
  if (aspectRatio < TIRE_DIMENSION_BOUNDS.aspectRatio.min || aspectRatio > TIRE_DIMENSION_BOUNDS.aspectRatio.max) {
    return `Profile must be ${TIRE_DIMENSION_BOUNDS.aspectRatio.min}-${TIRE_DIMENSION_BOUNDS.aspectRatio.max}`;
  }
  if (rimInch < TIRE_DIMENSION_BOUNDS.rimInch.min || rimInch > TIRE_DIMENSION_BOUNDS.rimInch.max) {
    return `Rim must be ${TIRE_DIMENSION_BOUNDS.rimInch.min}-${TIRE_DIMENSION_BOUNDS.rimInch.max} in`;
  }
  return null;
};

export const tireDimensionBreakdownItems = (parts: TireDimensionParts): TireDimensionBreakdownItem[] => {
  const constructionExtra = isTireConstruction(parts.construction)
    ? TIRE_CONSTRUCTION_LABELS[parts.construction]
    : undefined;
  return [
    { meaning: 'Tire width', value: parts.widthMm, extra: ' mm' },
    { meaning: 'Tire profile', value: parts.aspectRatio, extra: '% of width' },
    {
      meaning: 'Construction',
      value: parts.construction,
      extra: constructionExtra ? ` (${constructionExtra})` : undefined
    },
    { meaning: 'Rim diameter', value: parts.rimInch, extra: ' in' }
  ];
};

export const splitTireDimensionText = (text: string): TireDimensionTextPart[] => {
  tireDimensionInTextRegex.lastIndex = 0;
  const matches = [...text.matchAll(tireDimensionInTextRegex)];
  if (matches.length === 0) {
    return [{ type: 'text', value: text }];
  }
  return matches.flatMap((match, index) => {
    const previous = matches[index - 1];
    const previousEnd = previous ? previous.index + previous[0].length : 0;
    const leading =
      match.index > previousEnd ? [{ type: 'text' as const, value: text.slice(previousEnd, match.index) }] : [];
    const trailing =
      index === matches.length - 1 && match.index + match[0].length < text.length
        ? [{ type: 'text' as const, value: text.slice(match.index + match[0].length) }]
        : [];
    return [...leading, { type: 'dimension' as const, value: match[0] }, ...trailing];
  });
};

export type CarTireWithIndices =
  | CarTireRecordList
  | (CarTireRecord & {
      loadIndex?: null | LoadIndexRecord;
      speedIndex?: null | SpeedIndexRecord;
    });

export const formatTireDimensionFromParts = (
  width: number | string,
  aspectRatio: number | string,
  rimInch: number | string,
  construction: TireConstruction = DEFAULT_TIRE_CONSTRUCTION
): string => `${width}/${aspectRatio}${construction}${rimInch}`;

export function getDimensionsString(tire: CarTireWithIndices): string {
  const profile = tire.dimensionProfile ?? '';
  const rim = tire.dimensionRimInch ?? '';
  return formatTireDimensionFromParts(tire.dimensionWidthMm ?? '', profile, rim);
}

export function getDimensionsLoadSpeedString(tire: CarTireWithIndices): string {
  return `${getDimensionsString(tire)} ${getLoadIndex(tire.loadIndex)} ${getSpeedIndex(tire.speedIndex)} `;
}

export function getQuantityOptionsEnglish(quantityMax: number) {
  return Array.from({ length: quantityMax }, (_, i) => ({
    label: `${i + 1} pcs`,
    value: i + 1
  }));
}

export function getTirePrice(
  tireQuoteItem: TireInQuoteItem | TireQuoteItemTireRecord | TireQuoteItemTireRecordCustomer
): MoneyRecord {
  return tireQuoteItem?.priceAdHoc?.priceGross ?? tireQuoteItem.tireInventory.salesItem.price.priceGross;
}

export function getSpeedIndex(speedIndex: SpeedIndexRecord | null | undefined) {
  if (!speedIndex) {
    return 'N/A';
  }
  return `${speedIndex.index} (${speedIndex.speed.value} km/h)`;
}

export function getLoadIndex(loadIndex: LoadIndexRecord | null | undefined) {
  if (!loadIndex) {
    return 'N/A';
  }
  return `${loadIndex.index} (${loadIndex.weight.value} kg)`;
}

// Export CarTireBase for use in components

export const formatTireDimension = (value: string, construction: TireConstruction = detectTireConstruction(value)) => {
  const digits = value.replace(/\D/g, '');
  const width = digits.slice(0, 3);
  const profile = digits.slice(3, 5);
  const rim = digits.slice(5, 7);
  if (digits.length <= 3) {
    return width;
  }
  // Reintroducing a deleted construction letter traps backspace at the completed profile.
  if (
    digits.length < 5 ||
    (digits.length === 5 && construction === DEFAULT_TIRE_CONSTRUCTION && !/[RDB]/i.test(value))
  ) {
    return `${width}/${profile}`;
  }
  return `${width}/${profile}${construction}${rim}`;
};

export type TireDimensionInputParts = {
  width: string;
  profile: string;
  rim: string;
  construction: TireConstruction;
};

export const TIRE_DIMENSION_PART_MAX = {
  width: 3,
  profile: 2,
  rim: 2
} as const;

export type TireDimensionPart = keyof typeof TIRE_DIMENSION_PART_MAX;

export const TIRE_DIMENSION_PART_ORDER: TireDimensionPart[] = ['width', 'profile', 'rim'];

// What fits a passenger car or van; wider than the backend bounds' sanity check, which only stops nonsense.
const TIRE_DIMENSION_TYPICAL_RANGE: Record<TireDimensionPart, { min: number; max: number }> = {
  width: { min: 125, max: 355 },
  profile: { min: 25, max: 85 },
  rim: { min: 12, max: 24 }
};

/** The first fully typed part outside what a passenger car usually has, with that range; null when none is. */
export const getTireDimensionPartOutsideTypicalRange = (
  value: string
): { part: TireDimensionPart; min: number; max: number } | null => {
  const parts = tireDimensionPartsFromValue(value);
  for (const part of TIRE_DIMENSION_PART_ORDER) {
    const { min, max } = TIRE_DIMENSION_TYPICAL_RANGE[part];
    const typed = parts[part];
    if (typed.length === TIRE_DIMENSION_PART_MAX[part] && (Number(typed) < min || Number(typed) > max)) {
      return { part, min, max };
    }
  }
  return null;
};

// A size typed box by box, with every separator from the first non-empty later part on, so a box emptied in the
// middle ("/55R16") keeps the boxes after it instead of shifting their digits forward.
const SEPARATED_TIRE_DIMENSION_REGEX = /^(\d*)\/(\d*)([RDB]?)(\d*)$/i;

const serializeTireDimensionParts = ({ width, profile, rim, construction }: TireDimensionInputParts): string => {
  const keepsConstruction = rim !== '' || construction !== DEFAULT_TIRE_CONSTRUCTION;
  if (profile === '' && !keepsConstruction) {
    return width;
  }
  return `${width}/${profile}${keepsConstruction ? `${construction}${rim}` : ''}`;
};

export const tireDimensionPartsFromValue = (value: string): TireDimensionInputParts => {
  const construction = detectTireConstruction(value);
  const separated = value.match(SEPARATED_TIRE_DIMENSION_REGEX);
  if (separated) {
    return { width: separated[1] ?? '', profile: separated[2] ?? '', rim: separated[4] ?? '', construction };
  }
  // Digits without separators (pasted or typed in one field) fill the boxes in order.
  const formatted = formatTireDimension(value, construction);
  const slash = formatted.indexOf('/');
  const letterIndex = formatted.search(/[RDB]/);
  if (slash === -1) {
    return { width: formatted, profile: '', rim: '', construction };
  }
  if (letterIndex === -1) {
    return { width: formatted.slice(0, slash), profile: formatted.slice(slash + 1), rim: '', construction };
  }
  return {
    width: formatted.slice(0, slash),
    profile: formatted.slice(slash + 1, letterIndex),
    rim: formatted.slice(letterIndex + 1),
    construction
  };
};

/** Typing into one box: digits past its length spill into the boxes after it. */
export const applyTireDimensionPartInput = (
  current: string,
  part: TireDimensionPart,
  raw: string,
  construction: TireConstruction = /[RDB]/i.test(raw) ? detectTireConstruction(raw) : detectTireConstruction(current)
): string => {
  const parts = tireDimensionPartsFromValue(current);
  const start = TIRE_DIMENSION_PART_ORDER.indexOf(part);
  const next = TIRE_DIMENSION_PART_ORDER.slice(start).reduce(
    (acc, key, offset) => {
      if (offset > 0 && acc.remaining.length === 0) {
        return acc;
      }
      const max = TIRE_DIMENSION_PART_MAX[key];
      return {
        values: { ...acc.values, [key]: acc.remaining.slice(0, max) },
        remaining: acc.remaining.slice(max)
      };
    },
    { values: { ...parts }, remaining: raw.replace(/\D/g, '') }
  ).values;

  return serializeTireDimensionParts({ ...next, construction });
};

/** The box the last of `digits` digits typed into `from` lands in, after spilling over the boxes it fills. */
export const getLastTireDimensionPartReached = (from: TireDimensionPart, digits: number): TireDimensionPart =>
  TIRE_DIMENSION_PART_ORDER.slice(TIRE_DIMENSION_PART_ORDER.indexOf(from)).reduce(
    (acc, part, offset) =>
      offset === 0 || acc.remaining > 0 ? { part, remaining: acc.remaining - TIRE_DIMENSION_PART_MAX[part] } : acc,
    { part: from, remaining: digits }
  ).part;

export const applyTireDimensionConstruction = (current: string, construction: TireConstruction): string =>
  serializeTireDimensionParts({ ...tireDimensionPartsFromValue(current), construction });

export const getTireBrandName = (tire: { brand?: { name: string } | null }) => tire.brand?.name ?? '';

export const getTireBrandAndModel = (tire: { brand?: { name: string } | null; model?: string | null }) =>
  [getTireBrandName(tire), tire.model].filter(Boolean).join(' ');
